/**
 * Inspection events — backing stages 9 (Inspection), 11 (Re-inspection), and
 * 13 (Functional Check / Re-functional Check).
 *
 * Each event records: date, outcome, optional notes, conditionally-required
 * resolved date, and (for stages 9 and 11) the inspection location.
 *
 * The `eventRound` field exists to support multiple rounds. Stage 11 still
 * caps at round 1 in v1; stage 13 uses round 2 for Re-functional Check.
 */

import { Type, type Static } from '@sinclair/typebox';
import { InspectionLocations, InspectionOutcomes } from '../enums.js';
import { FIELD_LIMITS } from '../constants.js';
import { IsoDateSchema, RowVersionField, TimestampFields, UuidSchema } from './common.js';

export const OUTCOME_BEARING_STAGES = [9, 11, 13] as const;
export type OutcomeBearingStageNumber = (typeof OUTCOME_BEARING_STAGES)[number];

// ---------------------------------------------------------------------------
// Read shape
// ---------------------------------------------------------------------------

export const InspectionEventSchema = Type.Object({
  id: UuidSchema,
  projectItemId: UuidSchema,
  stageNumber: Type.Union(OUTCOME_BEARING_STAGES.map((n) => Type.Literal(n))),
  /** 1 = primary; 2+ = subsequent rounds (re-inspection only). */
  eventRound: Type.Integer({ minimum: 1 }),

  eventDate: IsoDateSchema,
  outcome: Type.Union(InspectionOutcomes.map((o) => Type.Literal(o))),
  notes: Type.Union([Type.String({ maxLength: FIELD_LIMITS.INSPECTION_NOTES_MAX }), Type.Null()]),
  /** Required when outcome ∈ {OBSERVATION, REJECTED}; null when ACCEPTED. */
  resolvedDate: Type.Union([IsoDateSchema, Type.Null()]),
  /** Set only for stages 9 and 11. Null for stage 13 (functional check happens wherever delivery happened). */
  location: Type.Union([Type.Union(InspectionLocations.map((l) => Type.Literal(l))), Type.Null()]),

  recordedByUserId: UuidSchema,
  ...TimestampFields,
  ...RowVersionField,
});
export type InspectionEvent = Static<typeof InspectionEventSchema>;

// ---------------------------------------------------------------------------
// Create input
// ---------------------------------------------------------------------------

export const InspectionEventCreateSchema = Type.Object({
  stageNumber: Type.Union(OUTCOME_BEARING_STAGES.map((n) => Type.Literal(n))),
  eventDate: IsoDateSchema,
  outcome: Type.Union(InspectionOutcomes.map((o) => Type.Literal(o))),
  notes: Type.Optional(Type.String({ maxLength: FIELD_LIMITS.INSPECTION_NOTES_MAX })),
  /** Required when outcome ≠ ACCEPTED. Server enforces. */
  resolvedDate: Type.Optional(IsoDateSchema),
  /** Required for stages 9 and 11; ignored for stage 13. */
  location: Type.Optional(Type.Union(InspectionLocations.map((l) => Type.Literal(l)))),
});
export type InspectionEventCreate = Static<typeof InspectionEventCreateSchema>;

// ---------------------------------------------------------------------------
// Update input — used to set the resolved date later, or correct details
// ---------------------------------------------------------------------------

export const InspectionEventUpdateSchema = Type.Object({
  eventDate: Type.Optional(IsoDateSchema),
  outcome: Type.Optional(Type.Union(InspectionOutcomes.map((o) => Type.Literal(o)))),
  notes: Type.Optional(
    Type.Union([Type.String({ maxLength: FIELD_LIMITS.INSPECTION_NOTES_MAX }), Type.Null()]),
  ),
  resolvedDate: Type.Optional(Type.Union([IsoDateSchema, Type.Null()])),
  location: Type.Optional(
    Type.Union([Type.Union(InspectionLocations.map((l) => Type.Literal(l))), Type.Null()]),
  ),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type InspectionEventUpdate = Static<typeof InspectionEventUpdateSchema>;
